import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';

import { TerraIconModule } from '@ninety/terra';
import { ButtonComponent } from '@ninety/ui/core';

import { NativeIntegrationCoreService } from '../../services/native-integration-core.service';
import { NativeIntegration } from '../../services/native.models';

/**
 * Lets the user pick which calendar provider to connect when more than one is available
 * (e.g. Google and Microsoft). Embedded content, not a MatDialog - meant to be swapped in place
 * of a host dialog's normal content (see MeetingBotCalendarSectionComponent's openProviderChooser),
 * not opened as its own nested modal, so the meeting dialog underneath stays in the background
 * instead of a second dialog stacking on top of it. Communicates back to whatever hosts it via
 * `closed`, the connected integration or undefined if the user cancelled - same nullable-result
 * contract SelectCalendarEventDialogComponent uses for the same reason.
 */
@Component({
  selector: 'ninety-choose-calendar-provider-dialog',
  standalone: true,
  imports: [CommonModule, TerraIconModule, ButtonComponent],
  templateUrl: './choose-calendar-provider-dialog.component.html',
  styles: [
    `
      @use 'terra';

      :host {
        display: flex;
        flex-direction: column;
        padding: 24px;
      }

      .choose-calendar-provider-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
      }

      .provider-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .provider-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        padding: 10px 12px;
        border: 1px solid terra.color('border');
        @include terra.radius(medium);
      }

      .provider-info {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
      }

      .provider-logo {
        width: 48px;
        height: 48px;
        flex-shrink: 0;
      }

      .provider-name {
        @include terra.text('body-semibold');
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .dialog-subtext {
        margin: 0 0 16px;
      }

      .text-light {
        color: terra.color('text', 'light');
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChooseCalendarProviderDialogComponent {
  private readonly nativeIntegrationService = inject(NativeIntegrationCoreService);
  private readonly destroyRef = inject(DestroyRef);

  readonly integrations = input.required<NativeIntegration[]>();

  /** Emits the connected integration, or undefined if the user cancelled. */
  readonly closed = output<NativeIntegration | undefined>();

  readonly connectingIntegrationId = signal<string | undefined>(undefined);

  // Matches the Figma-specified display order (Microsoft, then Google) rather than whatever
  // order the integrations happen to come back from the API in.
  private static readonly PROVIDER_ORDER = ['microsoft-calendar', 'google-calendar', 'google'];

  protected readonly sortedIntegrations = computed(() =>
    [...this.integrations()].sort((a, b) => {
      const rank = (integration: NativeIntegration) => {
        const provider = (integration.oauthType || integration.company)?.toLowerCase();
        const index = ChooseCalendarProviderDialogComponent.PROVIDER_ORDER.indexOf(provider);
        return index === -1 ? ChooseCalendarProviderDialogComponent.PROVIDER_ORDER.length : index;
      };
      return rank(a) - rank(b);
    })
  );

  connect(integration: NativeIntegration): void {
    this.connectingIntegrationId.set(integration.id);
    this.nativeIntegrationService
      .connectCalendarViaPopup(integration)
      .pipe(
        finalize(() => this.connectingIntegrationId.set(undefined)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        if (result === 'connected') {
          this.closed.emit(integration);
        }
      });
  }

  cancel(): void {
    this.closed.emit(undefined);
  }
}
